import { memo, useState } from 'react';
import { FiStar, FiShoppingCart, FiHeart, FiEye } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getBookImage, handleBookImageError } from '../../utils';
import QuickViewModal from '../quickview/QuickViewModal';
import StockOverlay from '../common/StockOverlay';
import PreorderBadge from '../common/PreorderBadge';
import { isPreorderActive } from '../../utils/preorder';
import { getPurchaseIssue } from '../../utils/stock';

const BookCard = ({ book }) => {
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const { addToast } = useToast();
  const { formatBookPrice, getBookAmount, getBookDiscount } = useCurrency();
  const marketDiscount = getBookDiscount(book);
  const inStock = !getPurchaseIssue({ ...book, quantity: 1 }, getBookAmount(book));
  const isPreorder = isPreorderActive(book);
  const [quickViewOpen, setQuickViewOpen] = useState(false);

  const inWishlist = isInWishlist(book.id);

  const handleWishlist = (e) => {
    e.preventDefault();
    if (inWishlist) {
      removeFromWishlist(book.id);
      addToast('Removed from wishlist', 'info');
    } else {
      addToWishlist(book);
      addToast('Added to wishlist ❤️', 'success');
    }
  };

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (!inStock) return;
    addToCart(book);
    addToast(`"${book.title}" added to cart`, 'success');
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickViewOpen(true);
  };

  return (
    <article className="group relative flex h-full min-w-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white p-2.5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg sm:p-3">
      {/* Wishlist Button */}
      <button
        onClick={handleWishlist}
        aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
        className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-md transition hover:bg-red-50 lg:opacity-0 lg:group-hover:opacity-100"
      >
        <FiHeart
          className={`text-sm ${inWishlist ? 'text-red-500 fill-red-500' : 'text-black'}`}
        />
      </button>

      {/* Book Image */}
      <Link to={`/book/${book.id}`} className="block relative">
        <figure className="mb-2.5 aspect-[3/4] overflow-hidden rounded-lg bg-gray-50 sm:mb-3">
          <img
            src={getBookImage(book.image)}
            onError={handleBookImageError}
            alt={book.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-contain p-2"
          />
          <StockOverlay stock={book.stock} />
        </figure>
        <button
          type="button"
          onClick={handleQuickView}
          aria-label={`Quick view ${book.title}`}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 inline-flex items-center gap-1.5 whitespace-nowrap bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-semibold px-4 py-2 rounded-full shadow-lg ring-1 ring-white/40 hover:from-orange-600 hover:to-orange-700 hover:shadow-xl transition-all duration-300"
        >
          <FiEye className="text-sm" />
          <span>Quick View</span>
        </button>
      </Link>

      <div className="mb-1"><PreorderBadge book={book} compact /></div>

      {/* Rating */}
      <aside
        className="flex items-center gap-1 mb-1"
        aria-label={`Rating: ${book.rating} out of 5`}
      >
        <span className="flex items-center gap-0.5">
          {[...Array(5)].map((_, i) => (
            <FiStar
              key={i}
              className={`text-xs ${
                i < Math.floor(book.rating) ? 'text-orange-400 fill-orange-400' : 'text-black'
              }`}
            />
          ))}
        </span>
        <span className="text-xs text-black">({book.reviewCount || (Array.isArray(book.reviews) ? book.reviews.length : 0)})</span>
      </aside>

      {/* Title */}
      <h3 className="text-sm font-medium text-black line-clamp-1 mb-0.5">
        <Link to={`/book/${book.id}`} className="hover:text-orange-600 transition">
          {book.title}
        </Link>
      </h3>

      {/* Author */}
      <p className="text-xs text-black mb-2 line-clamp-2 break-words">{book.author}</p>

      {/* Price */}
      <p className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-base font-semibold text-black">{formatBookPrice(book)}</span>
        {getBookAmount(book, 'originalPrice') > getBookAmount(book) && (
          <span className="text-sm text-black line-through">{formatBookPrice(book, 'originalPrice')}</span>
        )}
        {marketDiscount > 0 && (
          <span className="text-xs text-green-600 font-medium">({marketDiscount}% OFF)</span>
        )}
      </p>

      {/* Add to Cart Button */}
      <button
        onClick={handleAddToCart}
        disabled={!inStock}
        className="mt-auto flex min-h-11 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-orange-600 px-2 py-2.5 text-sm font-medium leading-5 text-white shadow-sm transition-colors hover:bg-orange-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600 sm:gap-2 sm:px-4 sm:text-base"
      >
        <FiShoppingCart className="shrink-0 text-base" aria-hidden="true" />
        <span>{inStock ? (isPreorder ? 'Pre-order' : 'Add to cart') : 'Coming soon'}</span>
      </button>

      {quickViewOpen && (
        <QuickViewModal book={book} onClose={() => setQuickViewOpen(false)} />
      )}
    </article>
  );
};

export default memo(BookCard);
